import { api } from './api';

// ============ Interfaces ============

export interface GeneralSettings {
    teamName: string;
    teamSlug: string;
    timezone: string;
    dateFormat: string;
    timeFormat: string;
    weekStartsOn: number;
}

export interface BrandingSettings {
    logo?: string;
    favicon?: string;
    primaryColor: string;
    accentColor: string;
    darkMode: string;
    emailSignature?: string;
    invoiceFooter?: string;
}

export interface NotificationSettings {
    emailNotifications: boolean;
    pushNotifications: boolean;
    digestFrequency: string;
    quietHoursStart?: string;
    quietHoursEnd?: string;
    partnershipUpdates: boolean;
    invoiceReminders: boolean;
    contractAlerts: boolean;
    projectDeadlines: boolean;
    paymentReceived: boolean;
    reviewRequests: boolean;
}

export interface IntegrationSettings {
    googleConnected: boolean;
    googleCalendarSync: boolean;
    slackWebhook?: string;
    stripeConnected: boolean;
    paypalEmail?: string;
    zapierEnabled: boolean;
}

export interface SecuritySettings {
    twoFactorRequired: boolean;
    sessionTimeout: number;
    ipWhitelist: string[];
    minPasswordLength: number;
    requireUppercase: boolean;
    requireNumbers: boolean;
    apiRateLimitPerMin: number;
}

export interface TeamSettings {
    id: string;
    teamId: string;
    general: GeneralSettings;
    branding: BrandingSettings;
    notifications: NotificationSettings;
    integrations: IntegrationSettings;
    security: SecuritySettings;
}

export interface UpdateTeamSettingsDto {
    general?: Partial<GeneralSettings>;
    branding?: Partial<BrandingSettings>;
    notifications?: Partial<NotificationSettings>;
    integrations?: Partial<IntegrationSettings>;
    security?: Partial<SecuritySettings>;
}

// ============ Helper ============

const normalizeId = (data: any): any => {
    if (!data) return data;
    if (Array.isArray(data)) {
        return data.map(normalizeId);
    }
    if (typeof data === 'object') {
        const { _id, ...rest } = data;
        return { id: _id || data.id, ...rest };
    }
    return data;
};

// ============ Service ============

export const settingsService = {
    getSettings: async (): Promise<TeamSettings> => {
        const response = await api.get('/settings');
        return normalizeId(response.data);
    },

    updateSettings: async (data: UpdateTeamSettingsDto): Promise<TeamSettings> => {
        const response = await api.patch('/settings', data);
        return normalizeId(response.data);
    },

    getSettingsByCategory: async (category: string): Promise<any> => {
        const response = await api.get(`/settings/${category}`);
        return normalizeId(response.data);
    },
};

export default settingsService;
